/**
 * Sticky Buy Bar
 *
 * Keeps an add to cart button in reach once the real one has scrolled out of
 * view, alongside the product name and its live price.
 *
 * Enabled per section in Product information → Sticky buy bar.
 *
 * Design notes:
 *
 *   - The bar never adds to cart itself. It forwards the click to the real
 *     submit button inside the product form, so variant selection, quantity,
 *     selling plans, loading states and error handling all keep working
 *     exactly as they do without it.
 *   - The price is mirrored from the section's own price block rather than
 *     rendered a second time, so sale prices, unit prices and currency
 *     formatting can never drift apart from the page.
 *   - Visibility is driven by an IntersectionObserver on the real button, so
 *     there is no scroll handler on the main thread.
 */

const VISIBLE = 'sticky-buy-bar--visible';

class StickyBuyBar extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {MutationObserver | undefined} */
  #stateObserver;

  /** @type {(() => void) | undefined} */
  #onVariantUpdate;

  /** The button currently being observed, so a morph can be detected. */
  #observed = null;

  connectedCallback() {
    const realButton = this.realButton;

    // No button to stand in for — stay out of the way entirely.
    if (!realButton) return;

    this.button?.addEventListener('click', this.#forwardClick);

    // Both observers must exist before the first #sync(), because #sync() binds
    // them to the button and will not rebind to the same node twice.
    this.#stateObserver = new MutationObserver(() => this.#syncButtonState());

    this.#observer = new IntersectionObserver(
      ([entry]) => this.classList.toggle(VISIBLE, !entry?.isIntersecting),
      { threshold: 0 }
    );

    this.#sync();

    this.#onVariantUpdate = () => requestAnimationFrame(() => this.#sync());
    document.addEventListener('variant:update', this.#onVariantUpdate);
    document.addEventListener('cart:update', this.#onVariantUpdate);
  }

  /**
   * Points both observers at the current button. A variant change morphs the
   * section, which swaps the button for a new node — the old one would still be
   * observed, leaving the bar stuck in whatever state it was last in.
   */
  #observe() {
    const realButton = this.realButton;

    if (!realButton || realButton === this.#observed) return;

    if (this.#observed) {
      this.#observer?.unobserve(this.#observed);
      this.#stateObserver?.disconnect();
    }

    this.#observed = realButton;
    this.#observer?.observe(realButton);

    // The disabled state also changes mid-submit, with no event to listen for.
    this.#stateObserver?.observe(realButton, { attributes: true, attributeFilter: ['disabled'] });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#stateObserver?.disconnect();
    this.button?.removeEventListener('click', this.#forwardClick);

    if (this.#onVariantUpdate) {
      document.removeEventListener('variant:update', this.#onVariantUpdate);
      document.removeEventListener('cart:update', this.#onVariantUpdate);
    }
  }

  /**
   * The section this bar belongs to. Looked up on each use because the theme
   * replaces the section's markup on a variant change.
   * @returns {ParentNode}
   */
  get scope() {
    return this.closest('.shopify-section') ?? document;
  }

  /** @returns {HTMLButtonElement | null} */
  get realButton() {
    return this.scope.querySelector('add-to-cart-component button[name="add"]');
  }

  /** @returns {HTMLButtonElement | null} */
  get button() {
    return this.querySelector('[data-sticky-buy-bar="button"]');
  }

  #forwardClick = () => {
    const realButton = this.realButton;

    if (!realButton || realButton.disabled) return;

    realButton.click();
  };

  /** Brings the price and the button state back in line with the page. */
  #sync() {
    this.#observe();

    // The bar renders a price container of its own, so only the page's counts.
    const price = Array.from(this.scope.querySelectorAll('[ref="priceContainer"]')).find(
      (element) => !this.contains(element)
    );
    const target = this.querySelector('[data-sticky-buy-bar="price"]');

    if (price && target && target.innerHTML !== price.innerHTML) {
      target.innerHTML = price.innerHTML;
    }

    this.#syncButtonState();
  }

  /** Mirrors the real button's label and availability. */
  #syncButtonState() {
    const realButton = this.realButton;
    const button = this.button;

    if (!realButton || !button) return;

    button.disabled = realButton.disabled;

    const label = realButton.querySelector('.add-to-cart-text__content')?.textContent?.trim();
    const target = this.querySelector('[data-sticky-buy-bar="label"]');

    if (label && target && target.textContent !== label) {
      target.textContent = label;
    }
  }
}

if (!customElements.get('sticky-buy-bar')) {
  customElements.define('sticky-buy-bar', StickyBuyBar);
}
